import { z } from "zod";

import { normalizedEmailSchema } from "./auth.js";

export const companyManagerRoles = ["client_manager", "vendor_manager"] as const;
export const companyManagerRoleSchema = z.enum(companyManagerRoles);

export const companyMembershipRoles = [
  "client_owner",
  "client_manager",
  "vendor_owner",
  "vendor_manager",
] as const;
export const companyMembershipRoleSchema = z.enum(companyMembershipRoles);

export const companyMembershipStatuses = ["active", "inactive"] as const;
export const companyMembershipStatusSchema = z.enum(companyMembershipStatuses);

export const companyProjectAccessScopes = [
  "all_company_projects",
  "assigned_projects",
] as const;
export const companyProjectAccessScopeSchema = z.enum(companyProjectAccessScopes);

export const companyMembershipSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  userId: z.string().uuid(),
  role: companyMembershipRoleSchema,
  status: companyMembershipStatusSchema,
  joinedAt: z.string().datetime(),
});

export const companyManagedMembershipSchema = companyMembershipSchema.extend({
  projectAccessScope: companyProjectAccessScopeSchema,
  deactivatedAt: z.string().datetime().nullable(),
});

export const companyMemberUserSchema = z.object({
  id: z.string().uuid(),
  email: normalizedEmailSchema,
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  status: z.enum(["pending_verification", "active", "blocked", "archived"]),
  emailVerified: z.boolean(),
});

export const companyMemberSchema = companyManagedMembershipSchema.extend({
  user: companyMemberUserSchema,
});

export const companyMemberListResponseSchema = z.object({
  items: z.array(companyMemberSchema),
  total: z.number().int().nonnegative(),
});

export const companyMemberUpdateRequestSchema = z
  .object({
    status: companyMembershipStatusSchema.optional(),
    role: companyManagerRoleSchema.optional(),
    projectAccessScope: companyProjectAccessScopeSchema.optional(),
    reason: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict()
  .refine(
    (input) =>
      input.status !== undefined ||
      input.role !== undefined ||
      input.projectAccessScope !== undefined,
    "At least one membership change is required",
  );

export type CompanyManagerRole = z.infer<typeof companyManagerRoleSchema>;
export type CompanyMembershipRole = z.infer<typeof companyMembershipRoleSchema>;
export type CompanyMembershipStatus = z.infer<typeof companyMembershipStatusSchema>;
export type CompanyProjectAccessScope = z.infer<typeof companyProjectAccessScopeSchema>;
export type CompanyMembership = z.infer<typeof companyMembershipSchema>;
export type CompanyManagedMembership = z.infer<typeof companyManagedMembershipSchema>;
export type CompanyMember = z.infer<typeof companyMemberSchema>;
export type CompanyMemberListResponse = z.infer<typeof companyMemberListResponseSchema>;
export type CompanyMemberUpdateRequest = z.infer<typeof companyMemberUpdateRequestSchema>;
